/**
 * The five headline figures for the selected rent month.
 *
 * THE THREE AXES ARE KEPT APART BY WHAT IS *NOT* HERE.
 * `cash_received_in_selected_month` is a KPI the backend publishes and this row
 * deliberately does not show. It lives on the `payment_date` axis — money that
 * ARRIVED in the month — while "Bezahlt" here is the current paid state of the
 * selected month's invoice cohort. Placed side by side under two four-letter
 * German labels the two would read as the same number with a rounding error.
 * The cash figure gets its own captioned card in the Zahlungen section instead.
 *
 * Every sparkline is real. `trend[]` carries seven money figures per month —
 * the four expected amounts plus `invoiced_total`, `paid_current` and
 * `open_current` — so each of the five tiles plots its OWN metric over the same
 * months. Nothing here is a decorative curve.
 *
 * The card labels say "(Mietmonat)" and "(Stand heute)" rather than the design
 * reference's "(TOTAL)": these are rent-month cohorts in their current state,
 * not portfolio-wide balances, and "TOTAL" invites exactly the reading the
 * whole contract is built to prevent.
 *
 * WHY A ZERO NEEDS A SENTENCE (UX-1).
 * `CHF 0.00` on the three ACTUAL tiles is fail-closed correctness that reads as
 * "there are no invoices". The contract says something narrower: nothing is
 * PROVEN to belong to this rent month, because the period comes solely from the
 * draft-create ledger. So a zero on those tiles gains one line saying exactly
 * that, and the zero itself stays — an explicit zero is the backend's statement
 * and replacing it with a dash would delete it.
 *
 * The ORG-WIDE half of the explanation is deliberately NOT repeated per tile.
 * `unattributed.mirror_invoices` is a portfolio-wide set of invoices that belong
 * to NO month, so a sentence about it inside a month-scoped card would invite the
 * one inference the whole contract forbids. It appears once, below the row, where
 * it can name its own scope — and where the single "Rechnungen prüfen" link has
 * one unambiguous accessible name instead of three identical ones.
 *
 * What this component still does NOT have is a per-month exclusion count: the
 * response carries no `excluded_by_status` / `non_chf` / `unknown_currency`
 * figure for the selected month. Nothing here may therefore say WHY the month is
 * empty, or how many of the org-wide invoices belong to it.
 */
import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  Banknote,
  CheckCircle2,
  Clock,
  FileText,
  Gift,
} from "lucide-react";
import { Area, AreaChart, ResponsiveContainer } from "recharts";

import {
  changeDisplay,
  fmtMoney,
  isExplicitZero,
  SERIES_COLOR,
  shortPeriodLabel,
  toChartNumber,
} from "./revenueAnalyticsFormat";

/**
 * The Bexio invoice list — the one existing surface that shows mirrored invoices.
 *
 * Not `/admin/invoices`: that is the LEGACY Vantio invoice table, a different
 * entity that has nothing to do with the Bexio mirror these figures come from.
 * And no query string: `BexioInvoicesPage` keeps its filters in component state
 * and reads no URL parameter, so a `?q=` would be silently ignored and the link
 * would promise a filtered list it cannot deliver.
 */
export const BEXIO_INVOICES_ROUTE = "/admin/bexio/invoices";

/** Said on an ACTUAL tile whose figure is an explicit zero. Names no month. */
const NO_ATTRIBUTION_NOTE = "Keine autoritativ zugeordneten Beträge belegt.";

/**
 * Tints written out per tone so Tailwind's JIT sees whole class strings.
 * Never interpolate these — the classes get purged.
 */
const TONE = {
  blue: {
    label: "text-blue-600 dark:text-blue-400",
    icon: "bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400",
  },
  violet: {
    label: "text-violet-600 dark:text-violet-400",
    icon: "bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-400",
  },
  sky: {
    label: "text-sky-700 dark:text-sky-400",
    icon: "bg-sky-50 text-sky-700 dark:bg-sky-500/10 dark:text-sky-400",
  },
  green: {
    label: "text-emerald-700 dark:text-emerald-400",
    icon: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400",
  },
  amber: {
    label: "text-amber-700 dark:text-amber-400",
    icon: "bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300",
  },
};

const DIRECTION_ICON = { up: ArrowUpRight, down: ArrowDownRight, flat: ArrowRight };
const DIRECTION_TEXT = {
  up: "text-emerald-700 dark:text-emerald-400",
  down: "text-red-700 dark:text-red-400",
  flat: "text-muted-foreground",
};

/**
 * The KPI row's contents, as data.
 *
 * `kpiKey` names the field read from `kpis` and `comparison`; `trendKey` names
 * the field read from each `trend[]` point. Both are the response's own field
 * names, so a tile cannot show a figure the contract does not publish.
 *
 * `attributionScoped` marks the three tiles whose figure depends on the ledger
 * proving a rent month. The two expected tiles come from `tenancy_revenue` and
 * are computed without consulting a single invoice, so a zero there means the
 * contracts really do expect nothing — an attribution note on those would explain
 * a mechanism that is not involved.
 */
export const KPI_CARDS = [
  {
    id: "expected_recurring_rent",
    label: "Erwartete Miete",
    kpiKey: "expected_recurring_rent",
    trendKey: "expected_recurring_rent",
    tone: "blue",
    color: SERIES_COLOR.expected,
    icon: Banknote,
    // Rent is a SUBSET of the recurring total (rent rows only, versus every
    // monetizable recurring row behind the rent gate). Showing "Miete" without
    // the wider figure leaves a difference on the page that no reader can
    // explain, so it rides along here rather than living only in a tooltip.
    secondaryKey: "expected_recurring_total",
    secondaryLabel: "Wiederkehrend gesamt",
  },
  {
    id: "expected_one_time",
    label: "Einmalige Gebühren",
    kpiKey: "expected_one_time",
    trendKey: "expected_one_time",
    tone: "violet",
    color: SERIES_COLOR.oneTime,
    icon: Gift,
  },
  {
    id: "invoiced_total",
    label: "Fakturiert (Mietmonat)",
    kpiKey: "invoiced_total",
    trendKey: "invoiced_total",
    tone: "sky",
    color: SERIES_COLOR.invoiced,
    icon: FileText,
    attributionScoped: true,
  },
  {
    id: "paid_current_for_service_period",
    label: "Bezahlt (Stand heute)",
    kpiKey: "paid_current_for_service_period",
    trendKey: "paid_current",
    tone: "green",
    color: SERIES_COLOR.paid,
    icon: CheckCircle2,
    attributionScoped: true,
  },
  {
    id: "open_current_for_service_period",
    label: "Offen (Mietmonat)",
    kpiKey: "open_current_for_service_period",
    trendKey: "open_current",
    tone: "amber",
    color: SERIES_COLOR.open,
    icon: Clock,
    attributionScoped: true,
  },
];

function Sparkline({ points, color, label }) {
  if (!points || points.length < 2) {
    // One point is not a trend, and a flat line drawn from a single value would
    // claim a history that was never returned.
    return <div className="h-[38px]" aria-hidden="true" />;
  }
  return (
    <div className="h-[38px]" role="img" aria-label={label}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={points} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
          <Area
            type="monotone"
            dataKey="v"
            stroke={color}
            strokeWidth={1.75}
            fill={color}
            fillOpacity={0.12}
            isAnimationActive={false}
            dot={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function KpiCard({ card, kpis, comparison, comparisonLabel, trend }) {
  const Icon = card.icon;
  const tone = TONE[card.tone] || TONE.blue;
  const raw = kpis?.[card.kpiKey];
  const change = changeDisplay(comparison?.[card.kpiKey]);
  const DirIcon = DIRECTION_ICON[change.direction] || ArrowRight;
  // Only where the mechanism applies, and only for a zero that is actually
  // present: an absent figure renders as a dash and is a different statement.
  const showAttributionNote = card.attributionScoped === true && isExplicitZero(raw);

  const points = (trend || []).map((p) => ({
    v: toChartNumber(p?.[card.trendKey]),
    label: shortPeriodLabel(p),
  }));

  return (
    <div className="flex flex-col rounded-xl border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-2">
        <span
          className={`text-[11px] font-bold uppercase tracking-[0.08em] ${tone.label}`}
        >
          {card.label}
        </span>
        <span
          aria-hidden="true"
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${tone.icon}`}
        >
          <Icon className="h-4 w-4" />
        </span>
      </div>

      <div className="mt-2 text-[26px] font-bold leading-tight text-foreground">
        {fmtMoney(raw)}
      </div>

      {/* Direction is carried by an arrow AND a word AND the colour, so the
          movement is readable without separating the two hues. */}
      <div className={`mt-1 flex items-center gap-1 text-xs ${DIRECTION_TEXT[change.direction]}`}>
        <DirIcon aria-hidden="true" className="h-3.5 w-3.5" />
        <span>{change.percentText}</span>
        <span className="text-muted-foreground">vs. {comparisonLabel}</span>
        <span className="sr-only">{change.srText}</span>
      </div>

      {card.secondaryKey ? (
        <div className="mt-1 text-xs text-muted-foreground">
          {card.secondaryLabel}: {fmtMoney(kpis?.[card.secondaryKey])}
        </div>
      ) : null}

      {/* One line, no month named and no invoice count: the response carries
          neither a per-month exclusion figure nor a per-month invoice total, so
          this says only what `invoiced_total == 0` actually means. */}
      {showAttributionNote ? (
        <p className="mt-1 text-xs text-muted-foreground">{NO_ATTRIBUTION_NOTE}</p>
      ) : null}

      <div className="mt-2">
        <Sparkline
          points={points}
          color={card.color}
          label={`Verlauf ${card.label} über ${points.length} Monate`}
        />
      </div>
    </div>
  );
}

/**
 * The org-wide half of the explanation, said once.
 *
 * Shown only when BOTH halves are true: an actual tile is at zero (so there is a
 * confusing figure on screen to explain) and the portfolio actually holds
 * invoices that belong to no month (so there is something to point at). With no
 * unattributed invoices the per-tile line stands alone — a link offered without a
 * reason is worse than no link.
 *
 * The second sentence is the load-bearing one. Naming the count without it would
 * let a reader conclude that those invoices are the missing figures of THIS
 * month, and the response cannot support that: an unattributed invoice belongs to
 * no rent month at all.
 */
function AttributionScopeNotice({ unattributedInvoiceCount }) {
  const n = unattributedInvoiceCount;
  return (
    <div className="mt-4 flex flex-wrap items-start gap-x-3 gap-y-2 rounded-xl border border-border bg-muted/40 px-4 py-3 text-xs text-muted-foreground">
      <p className="min-w-0 flex-1">
        Fakturiert, Bezahlt und Offen zeigen nur Rechnungen, die über den
        Draft-Ledger einem Mietmonat autoritativ zugeordnet sind. Org-weit sind{" "}
        <span className="font-medium text-foreground">
          {n} {n === 1 ? "Rechnung" : "Rechnungen"} ohne Mietmonat-Zuordnung
        </span>
        ; zu welchen Monaten sie gehören, ist nicht belegt.
      </p>
      <Link
        to={BEXIO_INVOICES_ROUTE}
        className="shrink-0 rounded font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        Rechnungen prüfen →
      </Link>
    </div>
  );
}

export default function RevenueKpiGrid({
  kpis,
  comparison,
  comparisonLabel,
  trend,
  unattributedInvoiceCount = 0,
}) {
  const anyActualZero = KPI_CARDS.some(
    (card) => card.attributionScoped === true && isExplicitZero(kpis?.[card.kpiKey])
  );

  return (
    <section aria-label="Kennzahlen des gewählten Mietmonats">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {KPI_CARDS.map((card) => (
          <KpiCard
            key={card.id}
            card={card}
            kpis={kpis}
            comparison={comparison}
            comparisonLabel={comparisonLabel}
            trend={trend}
          />
        ))}
      </div>

      {anyActualZero && unattributedInvoiceCount > 0 ? (
        <AttributionScopeNotice unattributedInvoiceCount={unattributedInvoiceCount} />
      ) : null}
    </section>
  );
}
